import { useState } from "react";
import Header from "../components/Header";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import api, { formatApiError } from "../lib/api";
import { Search, Clock, CheckCircle2, XCircle, MapPin } from "lucide-react";

const STATUS = {
  pending: {
    label: "Pendiente",
    color: "bg-amber-100 text-amber-800",
    icon: <Clock size={14} />,
  },
  approved: {
    label: "Aprobada",
    color: "bg-emerald-100 text-emerald-800",
    icon: <CheckCircle2 size={14} />,
  },
  rejected: {
    label: "Rechazada",
    color: "bg-red-100 text-red-800",
    icon: <XCircle size={14} />,
  },
};

const fmt = (iso) => {
  try {
    return new Date(iso).toLocaleString("es-MX", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
};

export default function StatusPage() {
  const [searchTerm, setSearchTerm] = useState("");
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const search = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      // Llamamos al nuevo endpoint de búsqueda
      const { data } = await api.get(
        `/reservations/search?q=${encodeURIComponent(searchTerm)}`
      );
      setItems(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="status-page" className="min-h-screen">
      <Header />
      <div className="max-w-3xl mx-auto px-6 md:px-10 py-14">
        <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-3 inline-flex items-center gap-1.5">
          <Search size={12} /> Mi reserva
        </p>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
          Consulta el estado de tu apartado
        </h1>
        <p className="text-slate-600 mt-2">
          Ingresa tu <strong>nombre</strong> o tu <strong>número de teléfono</strong> para ver el progreso de tu solicitud.
        </p>

        <form onSubmit={search} className="mt-8 flex gap-3">
          <div className="flex-1">
            <Label htmlFor="search" className="sr-only">
              Nombre o Teléfono
            </Label>
            <Input
              id="search"
              type="text"
              required
              placeholder="Ej. Juan Pérez o 7441234567"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="status-search-input"
            />
          </div>
          <Button
            type="submit"
            className="bg-slate-900 hover:bg-slate-800 px-8"
            disabled={loading}
            data-testid="status-search-btn"
          >
            {loading ? "Buscando…" : "Buscar"}
          </Button>
        </form>

        {error && (
          <p className="mt-3 text-sm text-red-600" data-testid="status-error">
            {error}
          </p>
        )}

        {items !== null && (
          <div className="mt-10 space-y-3">
            {items.length === 0 && (
              <div
                data-testid="status-empty"
                className="border border-dashed border-slate-300 rounded-md p-8 text-center text-slate-500"
              >
                No encontramos reservas con ese nombre o número. Verifica que esté bien escrito.
              </div>
            )}
            {items.map((r) => {
              const s = STATUS[r.status] || STATUS.pending;
              
              const displayLot = r.id_lots ? `Lote ${r.id_lots}` : r.lot_label;
              
              const displayClientName = r.cliente?.nombre 
                ? `${r.cliente.nombre} ${r.cliente.apellido_paterno || ''} ${r.cliente.apellido_materno || ''}`.trim()
                : `ID Cliente: ${r.id_client}`;

              return (
                <div
                  key={r.id}
                  data-testid={`reservation-card-${r.id}`}
                  className="border border-slate-200 bg-white rounded-md p-5 shadow-sm"
                >
                  <div className="flex items-start justify-between flex-wrap gap-3">
                    <div>
                      <p className="font-display text-lg font-semibold inline-flex items-center gap-2">
                        <MapPin size={16} className="text-blue-500"/> {displayLot} - {displayClientName}
                      </p>
                      <p className="text-sm text-slate-500 mt-0.5">
                        Solicitado el {fmt(r.created_at)}
                      </p>
                      {r.cliente?.telefono && (
                         <p className="text-xs text-slate-400 mt-1">Tel: {r.cliente.telefono}</p>
                      )}
                    </div>
                    <span
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${s.color}`}
                    >
                      {s.icon}
                      {s.label}
                    </span>
                  </div>
                  {r.notes && (
                    <p className="text-sm text-slate-600 mt-3 border-l-2 border-slate-200 pl-3">
                      {r.notes}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}